import type { Employee, User } from "./types";

// Shared read-side filter for request-like rows (time off, training,
// custom form submissions). Scopes what the signed-in user is
// allowed to see on each request list:
//
//   Owner / L1  → every row in the agency.
//   L2          → only rows for employees whose home department
//                 matches the L2's own home department. Mirrors
//                 how notifications already fan out for custom
//                 forms, so what an L2 is pinged about is also
//                 what they can see on the list.
//   L3 / L4     → only their own submissions.
//   No account  → nothing.
//
// `empById` can be a Map or a plain Record so callers already using
// either shape don't have to normalise. An L2 with no linked
// employee (so no home department) falls back to seeing nothing —
// safer than defaulting to the agency-wide view.
export function visibleRequestsFor<T extends { employeeId: string }>(
  me: User | null | undefined,
  items: T[],
  empById: Map<string, Employee> | Record<string, Employee | undefined>,
): T[] {
  if (!me) return [];
  const level = me.level;
  if (level === "owner" || level === "l1") return items;

  function lookupEmp(id: string): Employee | undefined {
    if (empById instanceof Map) return empById.get(id);
    return (empById as Record<string, Employee | undefined>)[id];
  }

  if (level === "l2") {
    const meEmp = me.employeeId ? lookupEmp(me.employeeId) : undefined;
    const myDept = meEmp?.departmentId ?? null;
    if (!myDept) return [];
    return items.filter((t) => {
      const emp = lookupEmp(t.employeeId);
      return emp?.departmentId === myDept;
    });
  }

  // L3 / L4 — own only.
  if (!me.employeeId) return [];
  return items.filter((t) => t.employeeId === me.employeeId);
}
